import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { FeedbackTemplateEditor } from '../components/FeedbackTemplateEditor';
import { TimePicker12 } from '../components/TimePicker12';
import { GraduationCap, Plus, Trash2, ArrowLeft, Save, Check, ChevronsUpDown, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_PRESETS = ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5'];

const emptySession = () => ({
  session_id: `new-${Math.random().toString(36).slice(2, 9)}`,
  name: '', department: '', owner_id: null, owner_name: '', owner_email: '',
  date: '', start_time: '', end_time: '', online_url: '',
  feedback_template_id: '', attendees: [], phase: '',
});

// Searchable multi-select for session attendees
const AttendeesMultiSelect = ({ employees, value = [], onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const toggle = (id) => onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" data-testid={testid} className="w-full justify-between font-normal">
          <span className="truncate">{value.length ? `${value.length} attendee(s)` : 'Select attendees'}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search employee..." />
          <CommandList>
            <CommandEmpty>No employee found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__select_all__" onSelect={() => onChange(value.length === employees.length ? [] : employees.map(e => e.employee_id))}>
                <Check className={`mr-2 h-4 w-4 ${employees.length > 0 && value.length === employees.length ? 'opacity-100' : 'opacity-0'}`} />
                <span className="text-sm font-medium">Select all</span>
              </CommandItem>
              {employees.map(e => (
                <CommandItem key={e.employee_id} value={`${e.full_name} ${e.email} ${e.department}`}
                  onSelect={() => toggle(e.employee_id)}>
                  <Check className={`mr-2 h-4 w-4 ${value.includes(e.employee_id) ? 'opacity-100' : 'opacity-0'}`} />
                  <div className="min-w-0"><p className="truncate text-sm">{e.full_name}</p><p className="truncate text-xs text-muted-foreground">{e.department}</p></div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Searchable single-select combobox for session owner
const OwnerCombobox = ({ employees, value, onSelect, testid }) => {
  const [open, setOpen] = useState(false);
  const selected = employees.find(e => e.employee_id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" data-testid={testid} className="w-full justify-between font-normal">
          <span className="truncate">{selected ? selected.full_name : 'Select owner...'}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search employee..." />
          <CommandList>
            <CommandEmpty>No employee found.</CommandEmpty>
            <CommandGroup>
              {employees.map(e => (
                <CommandItem key={e.employee_id} value={`${e.full_name} ${e.email} ${e.department}`}
                  onSelect={() => { onSelect(e); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === e.employee_id ? 'opacity-100' : 'opacity-0'}`} />
                  <div className="min-w-0">
                    <p className="truncate text-sm">{e.full_name}</p>
                    <p className="truncate text-xs text-muted-foreground">{e.department}</p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default function InductionBuilderPage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const { inductionId } = useParams();
  const isEdit = !!inductionId;

  const [meta, setMeta] = useState({ employees: [], departments: [] });
  const [feedbackTemplates, setFeedbackTemplates] = useState([]);
  const [title, setTitle] = useState('');
  const [joiningDate, setJoiningDate] = useState('');
  const [selectedEmpIds, setSelectedEmpIds] = useState([]);
  const [sessions, setSessions] = useState([emptySession()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  // Inline "create new feedback template" dialog state
  const [tplOpen, setTplOpen] = useState(false);
  const [tplSessionIdx, setTplSessionIdx] = useState(null);
  const [tplSaving, setTplSaving] = useState(false);

  const loadMeta = useCallback(async () => {
    const [mRes, fRes] = await Promise.all([
      fetch(`${API}/inductions/meta`, { headers: getAuthHeader() }),
      fetch(`${API}/induction-feedback-templates`, { headers: getAuthHeader() }),
    ]);
    if (mRes.ok) setMeta(await mRes.json());
    else toast.error('Only HR can create inductions');
    if (fRes.ok) setFeedbackTemplates(await fRes.json());
  }, [getAuthHeader]);

  const loadExisting = useCallback(async () => {
    if (!isEdit) return;
    const r = await fetch(`${API}/inductions/${inductionId}`, { headers: getAuthHeader() });
    if (r.ok) {
      const d = await r.json();
      setTitle(d.title || '');
      setJoiningDate(d.joining_date ? String(d.joining_date).slice(0, 10) : '');
      setSelectedEmpIds(d.employee_ids || []);
      setSessions((d.sessions && d.sessions.length ? d.sessions : [emptySession()]).map(s => ({
        ...s,
        online_url: s.online_url || s.meeting_url || '',
        attendees: (s.attendees && s.attendees.length)
          ? s.attendees.map(a => ({ employee_id: a.employee_id, mode: a.mode || 'Offline', location: a.location || '' }))
          : (s.attendee_ids || []).map(id => ({ employee_id: id, mode: 'Offline', location: '' })),
      })));
    }
  }, [isEdit, inductionId, getAuthHeader]);

  useEffect(() => {
    (async () => { setLoading(true); await loadMeta(); await loadExisting(); setLoading(false); })();
  }, [loadMeta, loadExisting]);

  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    if (!q) return meta.employees;
    return meta.employees.filter(e =>
      `${e.full_name} ${e.email} ${e.department}`.toLowerCase().includes(q));
  }, [meta.employees, empSearch]);

  const selectedEmps = meta.employees.filter(e => selectedEmpIds.includes(e.employee_id));

  const toggleEmp = (id) => setSelectedEmpIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const updateSession = (idx, patch) => setSessions(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  const addSession = () => setSessions(prev => [...prev, emptySession()]);
  const removeSession = (idx) => setSessions(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  // Feedback templates are assigned explicitly per session (no department auto-match).
  const onDeptChange = (idx, dept) => updateSession(idx, { department: dept });

  const openNewTemplate = (idx) => { setTplSessionIdx(idx); setTplOpen(true); };
  const saveNewTemplate = async (payload) => {
    setTplSaving(true);
    const r = await fetch(`${API}/induction-feedback-templates`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    setTplSaving(false);
    if (r.ok) {
      const t = await r.json();
      setFeedbackTemplates(prev => [t, ...prev]);
      if (tplSessionIdx != null) updateSession(tplSessionIdx, { feedback_template_id: t.template_id });
      toast.success('Template created & assigned to this session');
      setTplOpen(false);
    } else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed to create template'); }
  };

  const buildPayload = () => ({
    title: title.trim(),
    joining_date: joiningDate || null,
    employee_ids: selectedEmpIds,
    sessions: sessions
      .filter(s => (s.name || '').trim())
      .map(s => ({
        session_id: String(s.session_id).startsWith('new-') ? null : s.session_id,
        name: s.name, department: s.department, owner_id: s.owner_id,
        owner_name: s.owner_name, owner_email: s.owner_email,
        date: s.date || null, start_time: s.start_time, end_time: s.end_time,
        online_url: s.online_url || '',
        feedback_template_id: s.feedback_template_id || null,
        attendees: (s.attendees || []).map(a => ({ employee_id: a.employee_id, mode: a.mode || 'Offline', location: a.location || '' })),
        phase: s.phase || '',
      })),
  });

  const validate = (thenPublish) => {
    if (!title.trim()) { toast.error('Please enter a title'); return false; }
    if (selectedEmpIds.length === 0) { toast.error('Select at least one new joiner'); return false; }
    const valid = sessions.filter(s => (s.name || '').trim());
    if (valid.length === 0) { toast.error('Add at least one session with a name'); return false; }
    for (let i = 0; i < valid.length; i++) {
      const s = valid[i];
      const label = `Session ${i + 1}`;
      if (!s.owner_id) { toast.error(`${label}: select a Session Owner`); return false; }
      if (!s.department) { toast.error(`${label}: select a Department`); return false; }
      if (!s.feedback_template_id) { toast.error(`${label}: assign a Feedback Form`); return false; }
      if (!s.date) { toast.error(`${label}: set a Date`); return false; }
      if (!s.start_time) { toast.error(`${label}: set a start Time`); return false; }
      if (!(s.attendees && s.attendees.length)) { toast.error(`${label}: select at least one Attendee`); return false; }
    }
    return true;
  };

  const save = async (thenPublish = false) => {
    if (!validate(thenPublish)) return;
    // Owner double-booking conflict detection (non-blocking warning)
    try {
      const payload = buildPayload();
      const cr = await fetch(`${API}/inductions/check-conflicts`, {
        method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ induction_id: inductionId || null, sessions: payload.sessions }),
      });
      if (cr.ok) {
        const { conflicts } = await cr.json();
        if (conflicts && conflicts.length) {
          const lines = conflicts.map(c => `• ${c.owner_name} on ${c.date} ("${c.session_name}" clashes with "${c.conflict_session}" in ${c.conflict_induction})`).join('\n');
          if (!window.confirm(`Scheduling conflict detected:\n\n${lines}\n\nSave anyway?`)) return;
        }
      }
    } catch (_e) { /* non-blocking */ }
    setSaving(true);
    try {
      const payload = buildPayload();
      let id = inductionId;
      if (isEdit) {
        const r = await fetch(`${API}/inductions/${inductionId}`, { method: 'PUT', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Save failed'); }
      } else {
        const r = await fetch(`${API}/inductions`, { method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Create failed'); }
        const d = await r.json(); id = d.induction_id;
      }
      if (thenPublish) {
        const pr = await fetch(`${API}/inductions/${id}/publish`, { method: 'POST', headers: getAuthHeader() });
        if (!pr.ok) { const d = await pr.json().catch(() => ({})); throw new Error(d.detail || 'Publish failed'); }
        toast.success('Induction published');
      } else {
        toast.success(isEdit ? 'Induction saved' : 'Induction created');
      }
      navigate('/inductions');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Layout><div className="p-8 text-muted-foreground">Loading builder...</div></Layout>;

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="induction-builder">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/inductions')} data-testid="builder-back-btn"><ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <GraduationCap className="h-6 w-6" /> {isEdit ? 'Edit Induction' : 'Create Induction'}
            </h1>
            <p className="text-muted-foreground text-sm">Set up the schedule for new joiners.</p>
          </div>
        </div>

        {/* Details */}
        <Card>
          <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Induction Title *</Label>
              <Input data-testid="induction-title-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. September Batch — Sales Onboarding" className="mt-1.5" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Joining Date</Label>
                <Input type="date" data-testid="induction-joining-date" value={joiningDate} onChange={e => setJoiningDate(e.target.value)} className="mt-1.5" />
                <p className="text-xs text-muted-foreground mt-1">Drives the 45-day new-joiner visibility window.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* New joiners */}
        <Card>
          <CardHeader><CardTitle className="text-base">New Joiners *</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {selectedEmps.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="selected-joiners">
                {selectedEmps.map(e => (
                  <Badge key={e.employee_id} variant="secondary" className="gap-1.5 py-1">
                    {e.full_name}
                    <button onClick={() => toggleEmp(e.employee_id)} className="hover:text-rose-600"><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
            <Input placeholder="Search employees by name / email / department..." value={empSearch} onChange={e => setEmpSearch(e.target.value)} data-testid="joiner-search" />
            <ScrollArea className="h-52 border rounded-md">
              <div className="p-2 space-y-1">
                {filteredEmployees.map(e => (
                  <label key={e.employee_id} className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-accent cursor-pointer" data-testid={`joiner-option-${e.employee_id}`}>
                    <Checkbox checked={selectedEmpIds.includes(e.employee_id)} onCheckedChange={() => toggleEmp(e.employee_id)} />
                    <div className="min-w-0">
                      <p className="text-sm truncate">{e.full_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{e.department} · {e.email}</p>
                    </div>
                  </label>
                ))}
                {filteredEmployees.length === 0 && <p className="text-sm text-muted-foreground p-3">No employees match.</p>}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Sessions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-base">Sessions</CardTitle>
            <Button size="sm" variant="outline" onClick={addSession} data-testid="add-session-btn"><Plus className="h-4 w-4 mr-1" /> Add Session</Button>
          </CardHeader>
          <CardContent className="space-y-5">
            {sessions.map((s, idx) => (
              <div key={s.session_id} className="border rounded-lg p-4 space-y-4 relative" data-testid={`session-card-${idx}`}>
                <div className="flex items-center justify-between">
                  <Badge variant="outline">Session {idx + 1}</Badge>
                  {sessions.length > 1 && (
                    <Button size="icon" variant="ghost" onClick={() => removeSession(idx)} data-testid={`remove-session-${idx}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Session Name *</Label>
                    <Input value={s.name} onChange={e => updateSession(idx, { name: e.target.value })} data-testid={`session-name-${idx}`} placeholder="e.g. HR Policies & Benefits" className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Department *</Label>
                    <Select value={s.department || undefined} onValueChange={v => onDeptChange(idx, v)}>
                      <SelectTrigger className="mt-1.5" data-testid={`session-dept-${idx}`}><SelectValue placeholder="Select department" /></SelectTrigger>
                      <SelectContent>
                        {meta.departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Feedback Form *</Label>
                    <Select value={s.feedback_template_id || undefined} onValueChange={v => updateSession(idx, { feedback_template_id: v })}>
                      <SelectTrigger className="mt-1.5" data-testid={`session-feedback-${idx}`}><SelectValue placeholder="Select feedback form" /></SelectTrigger>
                      <SelectContent>
                        {feedbackTemplates.length === 0
                          ? <div className="px-2 py-1.5 text-xs text-muted-foreground">No forms yet — create one below</div>
                          : feedbackTemplates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button type="button" size="sm" variant="ghost" className="mt-1.5 h-7 px-2 text-xs text-primary" onClick={() => openNewTemplate(idx)} data-testid={`create-template-${idx}`}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Create new feedback form
                    </Button>
                  </div>
                  <div>
                    <Label>Session Owner *</Label>
                    <div className="mt-1.5">
                      <OwnerCombobox employees={meta.employees} value={s.owner_id} testid={`session-owner-${idx}`}
                        onSelect={(e) => updateSession(idx, { owner_id: e.employee_id, owner_name: e.full_name, owner_email: e.email })} />
                    </div>
                  </div>
                  <div>
                    <Label>Date *</Label>
                    <Input type="date" value={s.date ? String(s.date).slice(0, 10) : ''} onChange={e => updateSession(idx, { date: e.target.value })} data-testid={`session-date-${idx}`} className="mt-1.5" />
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label>Start Time *</Label>
                      <div className="mt-1.5"><TimePicker12 value={s.start_time} onChange={v => updateSession(idx, { start_time: v })} testid={`session-start-${idx}`} /></div>
                    </div>
                    <div>
                      <Label>End Time</Label>
                      <div className="mt-1.5"><TimePicker12 value={s.end_time} onChange={v => updateSession(idx, { end_time: v })} testid={`session-end-${idx}`} /></div>
                    </div>
                  </div>
                  <div>
                    <Label>Phase / Day</Label>
                    <Select value={PHASE_PRESETS.includes(s.phase) ? s.phase : '__custom__'} onValueChange={v => updateSession(idx, { phase: v === '__custom__' ? '' : v })}>
                      <SelectTrigger className="mt-1.5" data-testid={`session-phase-${idx}`}><SelectValue placeholder="Select phase" /></SelectTrigger>
                      <SelectContent>
                        {PHASE_PRESETS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        <SelectItem value="__custom__">Custom…</SelectItem>
                      </SelectContent>
                    </Select>
                    {!PHASE_PRESETS.includes(s.phase) && (
                      <Input value={s.phase || ''} onChange={e => updateSession(idx, { phase: e.target.value })} data-testid={`session-phase-custom-${idx}`} placeholder="Custom phase (e.g. Week 2)" className="mt-1.5" />
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Attendees *</Label>
                    <div className="mt-1.5">
                      <AttendeesMultiSelect employees={selectedEmps} value={(s.attendees || []).map(a => a.employee_id)} testid={`session-attendees-${idx}`}
                        onChange={(ids) => {
                          const ex = s.attendees || [];
                          updateSession(idx, { attendees: ids.map(id => ex.find(a => a.employee_id === id) || { employee_id: id, mode: 'Offline', location: '' }) });
                        }} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Select at least one attendee, then set each attendee's mode (Online / Offline).</p>
                    {(s.attendees || []).length > 0 && (
                      <div className="mt-3 space-y-2">
                        {(s.attendees || []).map(a => {
                          const emp = selectedEmps.find(e => e.employee_id === a.employee_id);
                          return (
                            <div key={a.employee_id} className="flex flex-col sm:flex-row sm:items-center gap-2 border rounded-md p-2 bg-muted/30" data-testid={`attendee-row-${idx}-${a.employee_id}`}>
                              <span className="text-sm flex-1 truncate">{emp?.full_name || a.employee_id}</span>
                              <div className="flex gap-1">
                                {['Offline', 'Online'].map(m => (
                                  <Button key={m} type="button" size="sm" variant={a.mode === m ? 'default' : 'outline'} className="h-7 text-xs"
                                    data-testid={`attendee-mode-${idx}-${a.employee_id}-${m.toLowerCase()}`}
                                    onClick={() => updateSession(idx, { attendees: s.attendees.map(x => x.employee_id === a.employee_id ? { ...x, mode: m } : x) })}>{m}</Button>
                                ))}
                              </div>
                              {a.mode === 'Offline' && (() => {
                                const opts = a.location && !(meta.locations || []).includes(a.location)
                                  ? [a.location, ...(meta.locations || [])] : (meta.locations || []);
                                return (
                                  <Select value={a.location || undefined}
                                    onValueChange={v => updateSession(idx, { attendees: s.attendees.map(x => x.employee_id === a.employee_id ? { ...x, location: v } : x) })}>
                                    <SelectTrigger className="h-8 sm:w-56 text-sm" data-testid={`attendee-location-${idx}-${a.employee_id}`}><SelectValue placeholder="Select location" /></SelectTrigger>
                                    <SelectContent>
                                      {opts.length === 0
                                        ? <div className="px-2 py-1.5 text-xs text-muted-foreground">No locations in Employee Master</div>
                                        : opts.map(loc => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {(s.attendees || []).some(a => a.mode === 'Online') && (
                      <div className="mt-3">
                        <Label>Meeting URL (shared by all online attendees)</Label>
                        <Input value={s.online_url || ''} onChange={e => updateSession(idx, { online_url: e.target.value })} data-testid={`session-url-${idx}`} placeholder="https://teams.microsoft.com/..." className="mt-1.5" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-3 pb-8">
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn"><Save className="h-4 w-4 mr-1.5" /> Save Draft</Button>
          <Button onClick={() => save(true)} disabled={saving} data-testid="save-publish-btn"><Check className="h-4 w-4 mr-1.5" /> Save & Publish</Button>
        </div>
      </div>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="inline-template-dialog">
          <DialogHeader>
            <DialogTitle>New Feedback Form</DialogTitle>
            <DialogDescription>Build a section-based feedback form and assign it to this session.</DialogDescription>
          </DialogHeader>
          {tplOpen && (
            <FeedbackTemplateEditor
              initial={null}
              saving={tplSaving}
              onSubmit={saveNewTemplate}
              onCancel={() => setTplOpen(false)}
              submitLabel="Create & Assign"
            />
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
